import { PricingModules, PricingModulesStatus, SYSTEM_MODE } from '../../../../context/contexts/PricingModuleContext';
import { LockedFeatureDisplayConfig } from '../../../common/LockedFeatures/types';
import {
    DisabledDisplayOptions,
    EnabledByModulesType,
    EnabledByModulesValue,
    RequiredModuleState,
    DisplayConfig
} from './types';

const isModuleActive = (
    moduleRequirement: EnabledByModulesValue,
    pricingModuleState: PricingModulesStatus[PricingModules]
): boolean => {
    const requiredState: RequiredModuleState =
        typeof moduleRequirement === 'string' ? moduleRequirement : moduleRequirement.requiredState;

    if (requiredState === RequiredModuleState.ANY_ENABLED) return pricingModuleState.anyEnabled;
    if (requiredState === RequiredModuleState.ON_PREM_ENABLED) return pricingModuleState.onPremEnabled;
    if (requiredState === RequiredModuleState.CLOUD_ENABLED) return pricingModuleState.cloudEnabled;
    if (requiredState === RequiredModuleState.ALL_ENABLED)
        return pricingModuleState.onPremEnabled && pricingModuleState.cloudEnabled;
    return false;
};

export const isAnyEnablingModulesActive = (
    enabledByModules: EnabledByModulesType,
    pricingModulesState: PricingModulesStatus,
    moduleMatchLogic: 'ALL' | 'ANY' = 'ANY'
) => {
    const checkFn = moduleMatchLogic === 'ALL' ? 'every' : 'some';

    return Object.keys(enabledByModules)[checkFn]((module) =>
        isModuleActive(enabledByModules[module], pricingModulesState[module])
    );
};

const getDisplayConfigForMissingModule = (
    enabledByModules: EnabledByModulesType,
    pricingModulesState: PricingModulesStatus
) => {
    for (const [module, config] of Object.entries(enabledByModules)) {
        const isActive = isModuleActive(config, pricingModulesState[module]);

        if (!isActive && typeof config !== 'string') return { ...config };
    }

    return null;
};

export const getDisplay = (
    id: string,
    pricingModulesState: PricingModulesStatus,
    mode: SYSTEM_MODE,
    hiddenConfig: Record<string, DisplayConfig>,
    isHiddenBySystemMode: (id: string, mode: SYSTEM_MODE) => boolean
): LockedFeatureDisplayConfig => {
    const isHidden = isHiddenBySystemMode(id, mode);
    if (isHidden) return { enabled: false, disabledDisplay: DisabledDisplayOptions.HIDDEN };
    if (!(id in hiddenConfig)) return { enabled: true };

    const topLevelConfig = hiddenConfig[id];
    const { enabledByModules, hiddenBySystemModes, moduleMatchLogic } = topLevelConfig;

    if (hiddenBySystemModes?.includes(mode)) return { enabled: false, disabledDisplay: DisabledDisplayOptions.HIDDEN };
    if (enabledByModules === undefined) return { enabled: true };
    if (isAnyEnablingModulesActive(enabledByModules, pricingModulesState, moduleMatchLogic)) {
        return { enabled: true };
    }

    const config = getDisplayConfigForMissingModule(enabledByModules, pricingModulesState) ?? topLevelConfig;
    const { disabledDisplayOption } = config;

    if (disabledDisplayOption === DisabledDisplayOptions.TOOLTIP) {
        return {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.TOOLTIP,
            tooltipProps: config.tooltipProps
        };
    }
    if (disabledDisplayOption === DisabledDisplayOptions.SHOWSTOPPER) {
        return {
            enabled: false,
            disabledDisplay: DisabledDisplayOptions.SHOWSTOPPER,
            showstopperProps: config.showstopperProps
        };
    }
    return { enabled: false, disabledDisplay: disabledDisplayOption };
};
